import {selectDefined} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import {StringIDFolder} from 'constants/FolderConstants';
import {FolderSummary} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import normalizeFolder from 'folders/util/normalizeFolder';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {confirm} from 'util/Modals';
import uniqueId from 'util/uniqueId';

import FolderDAO from './FolderDAO';

type StringIDFolderWithParentName = StringIDFolder & {parent: {name: string}};
interface StringIDFolderWithParentApiID {
  id: string;
  parent: {api_identifier: string};
}

export function confirmUngroupFolder(folder: StringIDFolderWithParentName): JQueryPromise<unknown> {
  const title = (
    <>
      <BenchlingIcon className="u-faded u-mini-spacing-right" icon={ModelTypeConstants.FOLDER} />
      Ungroup folder: {folder.name}
    </>
  );
  const text = (
    <>
      <p>
        Are you sure you want to ungroup? Ungrouping will move all of the items in this folder (including any
        subfolders) up one level to:{' '}
      </p>
      <p className="u-text-align-center">
        <BenchlingIcon
          className="u-faded u-mini-spacing-left u-mini-spacing-right"
          icon={ModelTypeConstants.FOLDER}
        />
        <span className="u-bold-font-weight">{folder.parent.name}</span>
      </p>
    </>
  );
  return confirm(title, text, ButtonStyle.NEGATIVE, {
    acceptText: 'Ungroup',
    enableRedesign: true,
  });
}

export async function ungroupFolder(folder: StringIDFolderWithParentName): Promise<void> {
  assert(folder.parent != null, 'Cannot ungroup a folder without a parent');

  const notificationId = uniqueId('folder-ungroup-');
  notifications.info(`Ungrouping "${folder.name}"...`, {id: notificationId, duration: 0});

  await FolderDAO.ungroupFolder(folder.id);

  notifications.success(`Successfully ungrouped "${folder.name}".`, {id: notificationId});

  // Re-list parent folder to show results
  getStore().dispatch(SidepanelReduxActions.listFolder(normalizeFolder(folder.parent)));
}

export async function updateFolderCollaborations(
  folder: StringIDFolderWithParentApiID,
  collaborations: Array<Collaboration>
): Promise<void> {
  await updateFolder(folder, {collaborations});
}

export interface UpdateData {
  name?: string;
  description?: string | null;
  parentId?: string;
  collaborations?: Array<Collaboration>;
}

export function updateFolder(
  folder: StringIDFolderWithParentApiID,
  data: UpdateData
): Promise<FolderSummary> {
  assert(folder.parent != null, 'Cannot update a folder without a parent');

  return FolderDAO.update(
    folder.id,
    selectDefined({
      ...data,
      collaborations: data.collaborations
        ? convertToCollaborationCreateOrUpdates(data.collaborations)
        : undefined,
    }),
    {
      parentFolderId: folder.parent.api_identifier,
    }
  );
}
